import React from 'react';
import { Stack, CircularProgress } from '@mui/material';
// import SearchIcon from '@mui/icons-material/Search';
import { CssTextField } from 'components/select';
import { useRecoilState } from 'recoil';
import { OrderPayload, isServiceAccountValid } from 'pages/myidm/store';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import ProvisioningStyles from 'pages/myidm/style';
import PropTypes from 'prop-types';

const ServiceAccountAdValidation = ({ localMembers }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [, setIsServiceAccountValid] = useRecoilState(isServiceAccountValid);
    const [inputValue, setInputValue] = React.useState('');
    const isAlreadyInAdGroup = (serviceAccount) =>
        order?.adGroupMembers?.length > 0 && order?.adGroupMembers?.some((member) => member?.gpid?.toLowerCase() === serviceAccount?.toLowerCase());
    const isAlreadyInLocalList = (serviceAccount) => localMembers?.some((element) => element?.gpid?.toLowerCase() === serviceAccount?.toLowerCase());

    const serviceAccApi = useMutation((payload) => axios.post(`${endPoints?.ssp?.kafka?.serviceAccountYesValidation}`, payload));
    const [lastValidatedValue, setLastValidatedValue] = React.useState('');

    // Sync inputValue with order.serviceAccount when it's cleared externally (e.g., by AdGroupMember)
    React.useEffect(() => {
        if (order?.serviceAccount === '' && inputValue !== '') {
            setInputValue('');
        }
    }, [order?.serviceAccount]);

    // Clear adGrpMember when user types in serviceAccount field AND validate regex pattern
    React.useEffect(() => {
        if (inputValue) {
            // Build the new order state
            const newOrderState = { ...order };

            // Clear adGrpMember fields if they exist
            if (order?.adGrpMember || order?.adGrpMemberDetails || order?.adGrpMemberValidation) {
                newOrderState.adGrpMember = null;
                newOrderState.adGrpMemberDetails = null;
                newOrderState.crossDomainUser = null;
                newOrderState.adGrpMemberValidation = { isValid: false, message: null };
            }

            // Validate regex pattern
            if (!/^[a-zA-Z0-9][a-zA-Z0-9_]{2,}$/.test(inputValue) || inputValue.includes('__') || inputValue.length > 209) {
                newOrderState.serviceAccount = inputValue;
                newOrderState.serviceAccountInputFieldValidation = {
                    isValid: false,
                    message: 'Service Account should be min. 3 chars, starts with alphanumeric and contains only underscore(_) as special char.',
                };
            } else if (isAlreadyInAdGroup(inputValue)) {
                // Check if already in AD Group members
                newOrderState.serviceAccount = inputValue;
                newOrderState.serviceAccountInputFieldValidation = {
                    isValid: false,
                    message: 'Member already present in the AD Group and please choose different member if you wish to continue further.',
                };
            } else if (isAlreadyInLocalList(inputValue)) {
                // Check if already in local list
                newOrderState.serviceAccount = inputValue;
                newOrderState.serviceAccountInputFieldValidation = {
                    isValid: false,
                    message: 'Service Account already added in the list.',
                };
            } else {
                // Clear validation error if pattern is valid
                newOrderState.serviceAccount = inputValue;
                newOrderState.serviceAccountInputFieldValidation = { isValid: true, message: '' };
            }

            setOrder(newOrderState);
        } else if (inputValue === '') {
            // Clear validation when input is empty - mark as invalid since no value
            setOrder({
                ...order,
                serviceAccount: '',
                serviceAccountInputFieldValidation: { isValid: false, message: '' },
            });
        }
    }, [inputValue]);

    // Automatically call API when serviceAccountInputFieldValidation is valid
    React.useEffect(() => {
        if (
            order?.serviceAccountInputFieldValidation?.isValid &&
            inputValue &&
            inputValue.length >= 3 && // Ensure at least 3 characters before calling API
            order?.environment &&
            inputValue !== lastValidatedValue // Prevent duplicate API calls
        ) {
            setLastValidatedValue(inputValue);
            setOrder({
                ...order,
                serviceAccount: inputValue,
                serviceAccountValidation: { isValid: false, message: '' },
            });
            const payload = {
                accountType: 'Active Directory',
                serviceAccountId: inputValue,
                environment: order?.environment,
            };
            serviceAccApi.mutate(payload);
        }
    }, [order?.serviceAccountInputFieldValidation?.isValid, inputValue, order?.environment]);

    // const handleSearch = () => {
    //     if (inputValue) {
    //         // Check if input field validation is already failing
    //         if (!order?.serviceAccountInputFieldValidation?.isValid) {
    //             // Don't proceed with API call if basic validations are failing
    //             return;
    //         }

    //         // If validations pass, call API
    //         if (order?.environment) {
    //             setOrder({
    //                 ...order,
    //                 serviceAccount: inputValue,
    //                 serviceAccountValidation: { isValid: false, message: '' },
    //             });
    //             const payload = {
    //                 accountType: 'Active Directory',
    //                 serviceAccountId: inputValue,
    //                 environment: order?.environment,
    //             };
    //             serviceAccApi.mutate(payload);
    //         }
    //     }
    // };
    React.useEffect(() => {
        if (serviceAccApi?.data?.data?.serviceAccountExist === false) {
            setOrder({
                ...order,
                serviceAccountValidation: { isValid: false, message: 'Given Service Account does not exist' },
            });
            setIsServiceAccountValid(true);
        }
        if (serviceAccApi?.data?.data?.serviceAccountExist === true) {
            setOrder({
                ...order,
                serviceAccountValidation: { isValid: true, message: '' },
            });
            setIsServiceAccountValid(false);
        }
    }, [serviceAccApi?.data?.data]);

    const validateInput = () => {
        const hasError = !order?.serviceAccountValidation?.isValid && order?.serviceAccountValidation?.message;
        if ((isTouched && order?.isTouched && !order?.serviceAccount) || hasError) {
            return 'validate';
        }
        return '';
    };

    React.useEffect(() => {
        if (!order?.serviceAccount && order?.isTouched) setIsTouched(true);
    }, [order?.serviceAccount, order?.isTouched]);

    // Check both input field validation and API validation for errors
    const inputFieldError = !order?.serviceAccountInputFieldValidation?.isValid && order?.serviceAccountInputFieldValidation?.message;
    const apiError = !order?.serviceAccountValidation?.isValid && order?.serviceAccountValidation?.message;
    const showError = inputFieldError || apiError;
    const errorMessage = inputFieldError || apiError;

    return (
        <Stack direction="row" spacing={1} sx={ProvisioningStyles.stack} width="100%">
            <CssTextField
                data-testid="service-acc-text"
                size="small"
                sx={{
                    width: (theme) => theme.spacing('100%'),
                    '& .MuiFormHelperText-root': {
                        marginTop: (theme) => theme.spacing('-2'),
                    },
                }}
                label="Service Account"
                autoComplete="off"
                error={showError}
                helperText={showError && <span style={ProvisioningStyles.disposal.helpText}>{errorMessage}</span>}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className={validateInput()}
                InputProps={{
                    endAdornment: (
                        <>
                            {serviceAccApi?.isLoading && <CircularProgress color="inherit" size={16} />}
                            {/* {serviceAccApi?.isLoading ? (
                                <CircularProgress color="inherit" size={16} />
                            ) : (
                                <SearchIcon sx={{ cursor: 'pointer', color: '#0047BA' }} onClick={handleSearch} data-testid="search-btn" />
                            )} */}
                        </>
                    ),
                }}
            />
        </Stack>
    );
};

ServiceAccountAdValidation.propTypes = {
    localMembers: PropTypes.array,
};

ServiceAccountAdValidation.defaultProps = {
    localMembers: [],
};

export default ServiceAccountAdValidation;
